<template>
  <div>
    <!-- loading - initial XHR -->
    <spinner-loader v-if="settingsLoading" />

    <!-- shared error state - initial shared XHR -->
    <callout v-if="error" type="error">
      <div>{{ error }}</div>
    </callout>

    <callout v-if="success" type="success">
      <div>{{ success }}</div>
    </callout>

    <!-- step 1 -->
    <intro-step
      v-if="isReady && step === 'intro'"
      :dietary-method="settings.energy.dietaryMethod"
      :energy-method="settings.energy.energyMethod"
      :calorie-type="selectedCalorieType"
      :from="from"
      :from-name="fromName"
      :deleting="deleting"
      @delete-action="deleteEnergy"
    />

    <!-- step 2 -->
    <dietary-step
      v-if="isReady && step === 'dietary'"
      :initial-dietary-method="settings.energy.dietaryMethod"
      :saving="saving"
      @save-action="saveEnergy"
    />

    <!-- step 3 -->
    <calorie-step
      v-if="isReady && step === 'calorie'"
      :dietary-method="settings.energy.dietaryMethod"
    />

    <!-- step 3a and step 3b -->
    <calorie-goal-step
      v-if="isReady && (step === 'range' || step === 'goal')"
      :calorie-types="calorieTypes"
      :initial-daily-calorie-type="settings.energy.dailyCalorieType"
      :saving="saving"
      @save-action="saveEnergy"
    />

    <!-- step 3c -->
    <calorie-calculator-step
      v-if="isReady && step === 'calculator'"
      :calorie-types="calorieTypes"
      :activity-levels="activityLevels"
      :measurement-systems="measurementSystems"
      :initial-measurement-system="user.measurementSystem"
      :initial-energy="settings.energy"
      :initial-consent="consents.USER_SETTINGS_ENERGY"
      :saving="saving"
      @save-action="saveEnergy"
    />
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import Callout from '@/components/notifications/callout.vue'
import SpinnerLoader from '@/components/loaders/spinner-loader.vue'
import IntroStep from './intro-step.vue'
import DietaryStep from './dietary-step.vue'
import CalorieStep from './calorie-step.vue'
import CalorieGoalStep from './calorie-goal-step.vue'
import CalorieCalculatorStep from './calorie-calculator-step.vue'
import { fbApi, objToSearchParams } from '@/utils/http-api'

/**
 * SPA that handles the user energy needs related to meal plans, covers
 * dietary method and energy method (calorie) to ultimately set
 * the user's calorie target.
 *
 * Steps
 * 1 = intro
 * 2 = dietary
 * 3 = calorie
 * 3a = calorie + range
 * 3b = calorie + goal
 * 3c = calorie + calculator
 */
export default defineComponent({
  name: 'EnergySettingsPage',
  components: {
    Callout,
    SpinnerLoader,
    IntroStep,
    DietaryStep,
    CalorieStep,
    CalorieGoalStep,
    CalorieCalculatorStep,
  },
  props: {},
  data() {
    // make sure only a pathname is saved
    let from = ''

    if (this.$route.query.from) {
      const fromUrl = new URL(this.$route.query.from, window.location.origin)

      from = `${fromUrl.pathname}${fromUrl.search}`

      // add anchor link if going back to calendar
      if (this.$route.query.fromName === 'Calendar') {
        from += '#meal-plans'
      }
    }

    return {
      settingsLoading: false, // initial load spinner
      saving: false, // individual step processing
      deleting: false,
      error: '',
      success: '',

      step: '', // see created() and syncUrlToStep()

      // url param to redirect user back to when finishing
      // window.history is not readable, so explicitly pass in via query param
      from,
      fromName: this.$route.query.fromName || '',

      // options set from API
      activityLevels: null,
      calorieTypes: null,
      measurementSystems: null,
    }
  },

  computed: {
    ...mapState(useUserAuthStore, ['user', 'settings', 'consents']),

    // making sure initial fetch data completely loaded
    isReady() {
      return !!(
        !this.settingsLoading &&
        this.activityLevels &&
        this.calorieTypes &&
        this.settings &&
        this.settings.energy
      )
    },

    selectedCalorieType() {
      const { dailyCalorieType } = this.settings.energy
      if (dailyCalorieType && this.calorieTypes[dailyCalorieType]) {
        return this.calorieTypes[dailyCalorieType]
      }

      return null
    },
  },

  // watchers
  watch: {
    // update step on route change and clear messages
    $route() {
      this.syncUrlToMessages()
      this.syncUrlToStep()
    },
  },

  async created() {
    this.syncUrlToStep()
    await this.fetchSettings()
  },

  methods: {
    ...mapActions(useUserAuthStore, ['updateUser', 'updateSettings', 'updateConsents']),

    /**
     * URL based error / success messages
     */
    syncUrlToMessages() {
      this.error = ''
      this.success = ''

      if (this.$route.query.success === 'dietary') {
        this.success = 'Successfully saved dietary approach'
      }

      if (this.$route.query.success === 'calorie') {
        this.success = 'Successfully saved daily energy needs'
      }
    },

    /**
     * Take :section and :energyMethod from url params and step
     * to SPA step.
     */
    syncUrlToStep() {
      // default to step 1
      let step = 'intro'

      if (this.$route.params) {
        // step 2 vs 3
        if (this.$route.params.section) {
          step = this.$route.params.section
        }

        // step 3a, 3b, 3c
        if (step === 'calorie' && this.$route.params.energyMethod) {
          step = this.$route.params.energyMethod
        }
      }

      this.step = step
    },

    /**
     * Fetch user settings for energy and calorie drop down options (same endpoint)
     */
    async fetchSettings() {
      if (this.settingsLoading === true) {
        return
      }

      this.settingsLoading = true
      this.error = ''
      this.success = ''

      try {
        const res = await fbApi.get('/my/settings', {
          searchParams: objToSearchParams({
            energy: 1,
          }),
        })

        // missing data
        if (!res || !res.body || !res.body.data) {
          throw new Error(`Unable to fetch energy settings`)
        }

        const { data } = res.body

        if (!data.options || !data.options.calorieTypes || !data.options.activityLevels) {
          throw new Error(`Unable to fetch energy setting options`)
        }
        // end checks

        // drop down options from API
        this.calorieTypes = data.options.calorieTypes
        this.activityLevels = data.options.activityLevels
        this.measurementSystems = data.options.measurementSystems

        // update both energy settings and consents in pinia
        const energy = data.settings.energy ? data.settings.energy : {}
        const consents = data.consents ? data.consents : {}

        this.updateSettings({ energy })
        this.updateConsents(consents)
      } catch (err) {
        this.error = `Unable to fetch energy settings, please refresh the page and try again`
      } finally {
        this.settingsLoading = false
      }
    },

    /**
     * XHR to update energy settings, used by most components
     */
    async saveEnergy({ formData, userData, nextRoute }) {
      if (this.saving === true) {
        return
      }

      this.saving = true
      this.error = ''
      this.success = ''

      try {
        const res = await fbApi.put('/my/settings/energy', {
          json: formData,
        })

        // missing data
        if (!res || !res.body || !res.body.data) {
          throw new Error(`Error saving energy`)
        }

        const { data } = res.body

        // update users profile (measurement_system)
        if (userData && userData.measurement_system) {
          await fbApi.put('/my/account', {
            json: userData,
          })

          this.updateUser({
            measurementSystem: userData.measurement_system,
          })
        }

        // prep for updating global state
        const energy = data.energy ? data.energy : {}
        const consents = data.consents ? data.consents : {}

        this.updateSettings({ energy })
        this.updateConsents(consents)

        if (nextRoute) {
          // 99% of the time this is a NavigationDuplicate error and floods sentry
          // if this action was clicked multiple times by user
          this.$router.push(nextRoute).catch(() => {})
        }
      } catch (err) {
        // leaving console log so sentry could pick it up
        console.error(err)
        this.error = `Unable to update energy settings, please refresh the page and try again`
      } finally {
        this.saving = false
      }
    },

    /**
     * XHR to delete energy settings complete
     */
    async deleteEnergy() {
      if (this.deleting) {
        return
      }

      // clear any existing error message
      this.deleting = true
      this.error = ''
      this.success = ''

      try {
        await fbApi.delete('/my/settings/energy')

        // update pinia, clearing out values
        this.updateSettings({
          energy: {},
        })

        this.success = 'Successfully cleared dietary and energy settings'
      } catch (err) {
        this.error = 'Unable to clear energy settings, please refresh the page and try again'
      } finally {
        this.deleting = false
      }
    },
  },
})
</script>
